/**
 * StepperInput - Numeric input with increment/decrement buttons.
 *
 * A compact stepper control for numeric values with optional suffix and hint text.
 * Supports both direct text entry and stepper buttons for flexibility.
 * Uses semantic tokens for consistent theming.
 */

import { useState, useRef } from 'react';
import { ChevronUp, ChevronDown, type LucideIcon } from 'lucide-react';
import { Label } from '@lean-agility/design-system/react';
import { cn } from '@lean-agility/design-system/react';

export interface StepperInputProps {
  /** Label text displayed above the input */
  label: string;
  /** Optional icon displayed before the label */
  icon?: LucideIcon;
  /** Current numeric value */
  value: number;
  /** Callback when value changes */
  onChange: (value: number) => void;
  /** Minimum allowed value */
  min?: number;
  /** Maximum allowed value */
  max?: number;
  /** Increment/decrement step size */
  step?: number;
  /** Suffix displayed after the value (e.g., '%') */
  suffix?: string;
  /** Hint text displayed below the input */
  hint?: string;
  /** Disable all interactions */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * StepperInput - Compact numeric stepper control.
 *
 * @example
 * ```tsx
 * <StepperInput
 *   label="Scale"
 *   icon={Zap}
 *   value={100}
 *   onChange={setScale}
 *   min={10}
 *   max={500}
 *   step={10}
 *   suffix="%"
 *   hint="Throughput multiplier"
 * />
 * ```
 */
export function StepperInput({
  label,
  icon: Icon,
  value,
  onChange,
  min = 1,
  max = 999,
  step = 1,
  suffix = '',
  hint,
  disabled = false,
  className,
}: StepperInputProps) {
  // Local state for the input value (allows typing invalid intermediate values)
  const [inputValue, setInputValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local state when the external value changes (e.g., from stepper buttons)
  // by adjusting state during render rather than in an effect.
  // See https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setInputValue(String(value));
  }

  const increment = () => {
    if (!disabled) {
      onChange(Math.min(max, value + step));
    }
  };

  const decrement = () => {
    if (!disabled) {
      onChange(Math.max(min, value - step));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow typing any value - validation happens on blur
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const parsed = parseFloat(inputValue);
    if (isNaN(parsed)) {
      // Reset to current value if invalid
      setInputValue(String(value));
    } else {
      // Clamp to min/max and update
      const clamped = Math.min(max, Math.max(min, parsed));
      onChange(clamped);
      setInputValue(String(clamped));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setInputValue(String(value));
      inputRef.current?.blur();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      increment();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      decrement();
    }
  };

  const buttonBaseClass = cn(
    `flex h-8.5 w-8 items-center justify-center border bg-transparent transition-colors`,
    'border-border-strong',
    disabled
      ? 'cursor-not-allowed text-text-disabled opacity-50'
      : `
        text-text-muted
        hover:bg-bg-muted hover:text-text-primary
        dark:hover:bg-bg-subtle
      `,
  );

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className="size-4 text-text-secondary" />}
        <Label>{label}</Label>
      </div>
      <div className="flex items-center gap-0">
        <button
          type="button"
          onClick={decrement}
          disabled={disabled || value <= min}
          aria-label={`Decrease ${label}`}
          className={cn(buttonBaseClass, 'rounded-l')}
        >
          <ChevronDown className="size-4" />
        </button>
        <div
          className={cn(
            `relative flex h-8.5 min-w-15 items-center justify-center border-y bg-transparent`,
            'border-border-strong',
          )}
        >
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            aria-label={label}
            className={cn(
              'size-full bg-transparent text-center text-sm outline-hidden',
              'focus:ring-1 focus:ring-action-primary focus:ring-inset',
              disabled ? 'cursor-not-allowed text-text-disabled opacity-50' : `text-text-primary`,
              suffix ? 'pr-0' : '',
            )}
            style={{
              // Dynamic width based on content length + some padding
              width: `${Math.max(40, (inputValue.length + (suffix ? suffix.length : 0)) * 9 + 16)}px`,
            }}
          />
          {suffix && (
            <span
              className={cn(
                'pointer-events-none pr-2 text-sm',
                disabled ? 'text-text-disabled opacity-50' : 'text-text-primary',
              )}
            >
              {suffix}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={increment}
          disabled={disabled || value >= max}
          aria-label={`Increase ${label}`}
          className={cn(buttonBaseClass, 'rounded-r')}
        >
          <ChevronUp className="size-4" />
        </button>
      </div>
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}
